/**
 * MAHI — client half of the shared event envelope (product doc Section 4).
 *
 * Owns the one thing only the client can decide: `session_id`. Per Section 4
 * it's "client-generated, tab-scoped" — sessionStorage (not localStorage) is
 * exactly that scope, and it survives a page navigation within the same tab
 * but not a new tab/window, which is what "one browsing sitting" means for a
 * web client.
 *
 * Session-boundary decision (product doc Section 11's open question,
 * resolved here): a clean `session.end` is not reliable even on this web
 * client — a closed tab, a lost connection, or a phone locked mid-scroll
 * never fires `beforeunload`/`pagehide` in every browser. So session length
 * is NOT computed from session.start→session.end. Instead:
 *   - `session.start` fires once per tab-scoped session_id.
 *   - `session.heartbeat` fires every HEARTBEAT_MS while the tab is visible.
 *   - `session.end` is still sent best-effort (a `pagehide` listener, via a
 *     keepalive fetch — see sendEndHint below) as a *hint*, but the nightly
 *     rollup job
 *     (backend/routes/cron.py::mahi_events_rollup) derives actual session
 *     length from last-heartbeat-or-event minus session.start, with an
 *     inactivity cutoff — the same "heartbeat + timeout" option Section 11
 *     lists, chosen over a hard end event because this app's own usage
 *     pattern (a swipe feed someone can background at any moment) is
 *     exactly the case that breaks a relied-upon end event.
 */
import mahi, { trackEvent } from './api';
import { getSessionId } from './session';

const HEARTBEAT_MS = 60_000;

let heartbeatTimer = null;

export { getSessionId };

/** Starts (idempotently) the session.start + heartbeat lifecycle for the
 * current tab. Safe to call on every app mount — a session_id already in
 * sessionStorage means this tab's session already started, so only the
 * heartbeat (re)attaches, no duplicate session.start fires. */
export function startSession() {
  if (!localStorage.getItem('mira_token')) return;
  const sessionId = getSessionId();
  const isNewSession = !sessionStorage.getItem('mira_session_started');
  if (isNewSession) {
    sessionStorage.setItem('mira_session_started', '1');
    trackEvent('session.start', {}, { sessionId });
  }

  if (heartbeatTimer) return; // already attached (e.g. a second provider mount in dev StrictMode)
  heartbeatTimer = setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    trackEvent('session.heartbeat', {}, { sessionId: getSessionId() });
  }, HEARTBEAT_MS);

  const sendEndHint = () => {
    const token = localStorage.getItem('mira_token');
    if (!token) return;
    // navigator.sendBeacon can't carry an Authorization header (and this
    // endpoint's auth dependency only reads the header), so a keepalive
    // fetch is used instead — still fires during unload in every browser
    // that matters here, unlike a plain axios call which unload can abort
    // mid-flight.
    try {
      fetch(`${mahi.defaults.baseURL}/events`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: 'session.end', payload: {}, session_id: getSessionId() }),
      }).catch(() => {});
    } catch { /* best-effort only — see this module's docstring on why
                 session.end is a hint, never relied on for session length */ }
  };
  window.addEventListener('pagehide', sendEndHint);
}
